'use client'

import type { HasUncontactedBestApplyResponseData } from '@app/api/v1/patients/applies/has-uncontacted-best-apply/types'
import { PatientBurgerMenu } from '@components/app/NavBar/PatientBurgerMenu'
import useLocalStorageState from '@components/hooks/useLocalStorageState'
import getHasUncontactedBestApply from '@lib/api/patients/applies/hasUncontactedBestApply'
import { getUnreadMessageCount } from '@lib/api/users/inbox/unread-message-count'
import LocalStorageItem from '@lib/browsers/localStorage/LocalStorageItem'
import type { ColorGroup } from '@lib/colors/colors'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const PATIENT_HAS_UNCONTACTED_BEST_APPLY_LOCAL_STORAGE_KEY =
  'patientHasUncontactedBestApply'

// Dynamically import the PatientBurgerMenu with Sendbird
const PatientBurgerMenuWithSendbird = dynamic(
  () =>
    import('./PatientBurgerMenuSendbirdWrapper.client').then((mod) => ({
      default: mod.PatientBurgerMenuSendbirdWrapper,
    })),
  {
    ssr: false,
  },
)

export function PatientBurgerMenuWrapper({
  colorGroup,
  shouldPollForUnreadMessages = false,
}: {
  colorGroup: ColorGroup
  shouldPollForUnreadMessages?: boolean
}) {
  // initialize state with existing values in LocalStorage
  const localStorageItem = new LocalStorageItem(
    PATIENT_HAS_UNCONTACTED_BEST_APPLY_LOCAL_STORAGE_KEY,
  )
  const stateFromExistingLocalStorage =
    localStorageItem.getItem<HasUncontactedBestApplyResponseData>() ?? {
      freshUntil: new Date().toISOString(),
      hasUncontactedBestApply: false,
    }

  const [unreadMessageCount, setUnreadMessageCount] = useState(0)
  const [uncontactedBestApply, setUncontactedBestApply] =
    useLocalStorageState<HasUncontactedBestApplyResponseData>(
      PATIENT_HAS_UNCONTACTED_BEST_APPLY_LOCAL_STORAGE_KEY,
      stateFromExistingLocalStorage,
    )
  const fetchUnreadMessageCount = async () => {
    const result = await getUnreadMessageCount()
    if (result.success && result.data !== undefined) {
      setUnreadMessageCount(result.data)
    }
  }

  const fetchHasUncontactedBestApply = async () => {
    // if the freshUntil is in the past, we need to fetch the data again
    if (new Date(uncontactedBestApply.freshUntil) < new Date()) {
      const result = await getHasUncontactedBestApply()
      if (result.success && result.data !== undefined) {
        setUncontactedBestApply(result.data)
      }
    }
  }

  useEffect(() => {
    fetchUnreadMessageCount()
    fetchHasUncontactedBestApply()
  }, [])

  if (shouldPollForUnreadMessages) {
    return (
      <PatientBurgerMenuWithSendbird
        colorGroup={colorGroup}
        hasUncontactedBestApply={uncontactedBestApply.hasUncontactedBestApply}
      />
    )
  }

  return (
    <PatientBurgerMenu
      colorGroup={colorGroup}
      hasUncontactedBestApply={uncontactedBestApply.hasUncontactedBestApply}
      unreadMessageCount={unreadMessageCount}
    />
  )
}
